import React, {
    createContext,
} from 'react';
import {
    configure,
    decorate,
    observable,
    autorun,
    when,
    runInAction,
    action
} from 'mobx';
import {
    loadCss,
    loadModules
} from "esri-loader";

import BasemapImage from './assets/basemap_imagery.jpg';
import RickLogo from './assets/LogoRick.png';
import { cookie, withCookies, Cookies } from 'react-cookie';
import { toast } from 'react-toastify';
import { notify, ToastTitle, ToastMessage } from 'calcite-react/Toaster';

configure({
    enforceActions: 'observed'
});
const cookies = new Cookies();

toast.configure();

export class MapData {
    isLoading = true;
    onboarded = cookies.get('onboarded') || false;
    webmap = null;
    highlight = null;
    editFeature = null;
    buildingsLayer = null;
    view = null;
    container = null;
    layerList = null;
    legend = null;
    measurement = null;
    popup = null;
    featureFormWidget = null;
    actionbar = {
        collapse: true,
        legend: false,
        layers: false,
        bookmarks: false,
        measure: false,
        filter: false,
        featureForm: false,
        download: false,
        information: true
    };



    showToaster = () => {
        //toast("Hello");
        notify(
            <React.Fragment>
                <ToastTitle>Getting Started</ToastTitle>
                <ToastMessage>Click on an index area on the map. Toggle additional functionality from the action bar on the left.</ToastMessage>
            </React.Fragment>,
            {
                position: 'bottom-center',
                autoClose: false,
            }
        );
    }

    loadMap = async (ref) => {
        await when(()=>loadModules([
            'esri/config',
            'dojo/dom-construct',
            'esri/core/watchUtils',
            'esri/core/urlUtils',
            'esri/portal/Portal',
            'esri/identity/OAuthInfo',
            'esri/identity/IdentityManager',
            'esri/WebMap',
            'esri/views/MapView',
            'esri/layers/support/TileInfo',
            'esri/widgets/Fullscreen',
            'esri/layers/GeoJSONLayer',
            'esri/widgets/Home',
            'esri/widgets/BasemapToggle',
            'esri/layers/support/LabelClass',
            'esri/PopupTemplate',
            'esri/Basemap',
            'esri/layers/TileLayer',
            'esri/layers/WebTileLayer',
        ], {
            css: true
        }).then(([esriConfig, domConstruct, watchUtils, urlUtils, Portal, OAuthInfo, identityManager, WebMap, MapView, TileInfo, Fullscreen, GeoJSONLayer, Home, BasemapToggle, LabelClass, PopupTemplate, Basemap, TileLayer, WebTileLayer]) => {
            esriConfig.portalUrl = 'https://portal2.rickengineering.com/portal';

            const info = new OAuthInfo({
                appId: 'ZgdjOXHhAxln1QIV',
                portalUrl: 'https://portal2.rickengineering.com/portal',
                popup: false
            });

            //identityManager.registerOAuthInfos([info]);
            //identityManager.checkSignInStatus('https://portal2.rickengineering.com/portal/sharing/rest/oauth2/authorize').then((cred) => {
                const webmap = new WebMap({
                    portalItem: {
                        id: 'c9477485aa294688b8e2fda5e23ed20b'
                    }
                });

                const view = new MapView({
                    map: webmap,
                    container: ref,
                    constraints: {
                        rotationEnabled: false,
                        lods: TileInfo.create().lods
                    }
                });
    
                const homeWidget = new Home({
                    view: view,
                });
                view.ui.add(homeWidget, {
                    position: 'top-left',
                    index: 0
                });
    
                // Image tiled layer.
                const imageryLayer = new TileLayer({
                    url: "http://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
                });
                const transportationLayer = new TileLayer({
                    url: "http://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer",
                    minScale: 6000
                });
                const imageryBasemap = new Basemap({
                    baseLayers: [transportationLayer, imageryLayer], 
                    title: "Imagery",
                    thumbnailUrl: BasemapImage
                });
                const basemapToggle = new BasemapToggle({
                    view: view,
                    nextBasemap: imageryBasemap,
                    titleVisible: true,
                });
                view.ui.add(basemapToggle, 'bottom-left');

                const labelClass = { // autocasts as new LabelClass()
                    symbol: {
                        type: "text", // autocasts as new TextSymbol()
                        color: "white",
                        haloColor: "black",
                        haloSize: "1px",
                        xoffset: 0,
                        yoffset: -8,
                        font: { // autocast as new Font()
                        family: "Arial",
                        size: 12,
                        weight: "bold"
                        }
                    },
                    labelPlacement: "above-center",
                    labelExpressionInfo: {
                        expression: "$feature.label"
                    }
                }

                const geoJSONLayer = new GeoJSONLayer({
                    url: "https://maps.rickengineering.com/beechwood/data/vpes.geojson",
                    copyright: "Virginia Peterson Elementary",
                    title: "Virginia Peterson Elementary",
                    // legendEnabled: false,
                    renderer: {
                        type: "simple",
                        symbol: {
                            type: "simple-marker",
                            size: 5,
                            color: [255, 170, 0],
                            outline: null
                        }
                    },
                    labelingInfo:[
                        { // autocasts as new LabelClass()
                            symbol: {
                                type: "text", // autocasts as new TextSymbol()
                                color: "orange",
                                haloColor: "black",
                                haloSize: "1.5px",
                                xoffset: 7,
                                yoffset: 7,
                                font: { // autocast as new Font()
                                family: "Arial",
                                size: 8,
                                weight: "bold"
                                }
                            },
                            labelPlacement: "below-left",
                            labelExpressionInfo: {
                                expression: `'Virginia Peterson' + TextFormatting.NewLine + 'Elementary School'`
                            }
                        }
                    ]
                });
                webmap.add(geoJSONLayer);

                const potreePopup = new PopupTemplate({ // autocasts as new PopupTemplate()
                    title: "Perimeter Street Section {label}",
                    content: [
                        {
                            type: "fields",
                            fieldInfos: [
                                {
                                    fieldName: "preferred_name",
                                    label: "Document"
                                },
                                {
                                    fieldName: "improvements",
                                    label: "Improvement Type"
                                }
                        ]
                        },
                        {
                            type: "text",
                            text: "{expression/desc}"
                        },
                        {
                            type: "media",
                            mediaInfos: [{
                                title: "",
                                type: "image",
                                caption: "",
                                value: {
                                    sourceURL: "{expression/thumbnail}",
                                    linkURL: "{expression/document}"
                                }
                            }]
                        }
                    ],
                    expressionInfos: [{
                        name: "desc",
                        expression: `if (IsEmpty($feature.docname)) {
                            return 'Desctription:' + TextFormatting.NewLine + $feature.filepath;
                        }  else {
                            return 'Click image for document:';
                        }`
                    },
                    {
                        name: "thumbnail",
                        expression: `if (IsEmpty($feature.docname)) {
                            return '';
                        }  else {
                            return 'https://maps.rickengineering.com/beechwood/images/thumbnails/' + $feature.docname + '.jpg';
                        }`
                    },
                    {
                        name: "document",
                        expression: `if (IsEmpty($feature.docname)) {
                            return '';
                        }  else {
                            return 'https://maps.rickengineering.com/beechwood/documents/' + $feature.docname + '.pdf';
                        }`
                    }]
                });

                const potreePopup2 = new PopupTemplate({ // autocasts as new PopupTemplate()
                    title: "Offsite Intersection {label}",
                    content: [
                        {
                            type: "fields",
                            fieldInfos: [
                                {
                                    fieldName: "preferred_name",
                                    label: "Document"
                                },
                                {
                                    fieldName: "feattype",
                                    label: "Type"
                                },
                                {
                                    fieldName: "improvements",
                                    label: "Improvement Type"
                                }
                        ]
                        },
                        {
                            type: "text",
                            text: "{expression/desc}"
                        },
                        {
                            type: "media",
                            mediaInfos: [{
                                title: "",
                                type: "image",
                                caption: "",
                                value: {
                                    sourceURL: "{expression/thumbnail}",
                                    linkURL: "{expression/document}"
                                }
                            }]
                        }
                    ],
                    expressionInfos: [{
                        name: "desc",
                        expression: `if (IsEmpty($feature.docname)) {
                            return 'Desctription:' + TextFormatting.NewLine + $feature.filepath;
                        }  else {
                            return 'Click image for document:';
                        }`
                    },
                    {
                        name: "thumbnail",
                        expression: `if (IsEmpty($feature.docname)) {
                            return '';
                        }  else {
                            return 'https://maps.rickengineering.com/beechwood/images/thumbnails/' + $feature.docname + '.jpg';
                        }`
                    },
                    {
                        name: "document",
                        expression: `if (IsEmpty($feature.docname)) {
                            return '';
                        }  else {
                            return 'https://maps.rickengineering.com/beechwood/documents/' + $feature.docname + '.pdf';
                        }`
                    }]
                });

                const rickLink = domConstruct.toDom("<a href='http://rickengineering.com' target='_blank'>" +
                "<img src='"+ RickLogo +"' id='rick_logo' title='RICK' style='cursor:pointer;height:5vh;'>" +
                "</a>");
                view.ui.add(rickLink, "bottom-right");

                webmap.when(() => {
                    //webmap.basemap = imageryBasemap;
                    webmap.layers.forEach((layer) => {
                        if (layer.title == "Road Improvements") {
                            layer.legendEnabled = false;
                        }
                        if (layer.type === "feature" && layer.title == "Perimeter Street Sections") {
                            //layer.labelingInfo = [labelClass];
                            layer.popupEnabled = true;
                            layer.popupTemplate = potreePopup;
                        } else if (layer.type === "feature" && layer.title == "Offsite Intersections") {
                            layer.labelingInfo = [labelClass];
                            layer.popupEnabled = true;
                            layer.popupTemplate = potreePopup2;
                        }
                    });
                });
    
                view.when().then(() => {
                    //console.log("map is ready");
                    //cookies.remove('onboarded', { path: '/' });
                    var params = urlUtils.urlToObject(window.location.href);
                    if (params != null && params.query != null) {
                        view.center = [parseFloat(params.query.lon), parseFloat(params.query.lat)];
                        view.zoom = parseInt(params.query.level);
                    }
                    if (!this.onboarded) {
                        this.showToaster();
                        cookies.remove('onboarded', true, { path: '/' });
                    }
                    cookies.set('onboarded', true, { path: '/' });

                    const fullscreen = new Fullscreen({
                        view: view
                    });

                    //*** Edits ***

                    autorun(() => {
                        view.on('click', (event) => {
                            if (this.actionbar.featureForm) {
//
                            }
                        });
                    });

                    runInAction(() => {
                        this.webmap = webmap;
                        this.view = view;
                        this.container = ref;
                        this.isLoading = false;
                        this.popup = potreePopup;
                    });
                }); 
        }));
    }

    setLayerList = (widget) => {
        runInAction(() => {
            this.layerList = widget;
        });
    }

    setSelectMeasure = (widget) => {
        runInAction(() => {
            this.measurement = widget;
        });
    }

    setEditFeature = (widget) => {
        runInAction(() => {
            this.featureFormWidget = widget;
        });
    }

    setActionBarCollapse = (e) => {
        e.preventDefault();
        runInAction(() => {
            this.actionbar.collapse = !this.actionbar.collapse;
        });
    }

    setSelectButton = (e) => {
        let target = e.currentTarget;
        if (target && target.id) {
            for (let [key, value] of Object.entries(this.actionbar)) {
                runInAction(() => {
                    if (key === target.id) {
                        this.actionbar[key] = !this.actionbar[key];
                    } else if (key === 'collapse') {
                        this.actionbar[key] = this.actionbar[key];
                    } else {
                        this.actionbar[key] = false;
                    }
                });
            }
        }
    }
}

decorate(MapData, {
    fromMap: observable,
    isLoading: observable,
    highlight: observable,
    editFeature: observable,
    buildingsLayer: observable,
    view: observable,
    featureForm: observable,
    container: observable,
    measurement: observable,
    popup: observable,
    actionbar: observable,
    loadMap: action,
    setLayerList: action,
    setSelectMeasure: action,
    setEditFeature: action,
    setActiveActionLayers: action,
    setActiveActionLegend: action,
    setActiveActionBookmarks: action,
    setActionBarCollapse: action,
    setActiveActionFeatureForm: action,
    setSelectButton: action
});

export default withCookies(createContext(new MapData()));